<template>
  <div id="app">
    <md-app
      md-mode="fixed"
      :class="{'new-dashboard-view': isDashboardRoute}"
    >
      <md-app-toolbar data-test-id="top-toolbar">
        <md-button
          v-if="isLoggedIn"
          class="md-icon-button mobile__menu-icon"
          data-test-id="menu-toggle-btn"
          @click="menuVisible = !menuVisible"
        >
          <md-icon data-test-id="menu-toggle-btn-icon">menu</md-icon>
        </md-button>
        <navbar />
      </md-app-toolbar>
      <!-- OLD DESIGN -->
      <!-- <md-app-drawer
        v-if="isLoggedIn"
        md-permanent="clipped"
        :md-active.sync="menuVisible"
        class="scrollbar"
      >
        <sidebar
          @hide-sidebar="hideSidebar"
          @show-popup="openPopup"
          @close-popup="closePopup"
        />
      </md-app-drawer> -->

      <!-- NEW DESIGN -->
      <md-app-drawer
        v-if="isLoggedIn"
        md-persistent="mini"
        :md-active.sync="menuVisible"
        data-test-id="app-sidebar"
      >
        <div
          v-if="!isForcedMenuHidden"
          class="app-menu-container"
          data-test-id="app-menu-container"
          ref="menuContainer"
        >
          <MainMenu />
          <AdditionalMenu />
        </div>
      </md-app-drawer>

      <md-app-content>
        <router-view/>
        <snackbar/>
        <inactivity-message/>
        <file-viewer/>
        <loader-bar/>
      </md-app-content>
    </md-app>
  </div>
</template>

<script>
  import Navbar from '@vue/navigation/Navbar.vue'
  import MainMenu from '@vue/navigation/mainMenu/MainMenu.vue'
  import AdditionalMenu from '@vue/navigation/AdditionalMenu.vue'
  // import Sidebar from '@vue/navigation/Sidebar.vue'
  import Snackbar from '@com/messages/Snackbar'
  import InactivityMessage from '@com/messages/InactivityMessage'
  import FileViewer from '@com/modals/FileViewer'
  import LoaderBar from '@com/messages/LoaderBar'

  import { vueRoutes } from '@router/const'

  import { mapGetters, mapActions } from 'vuex'
  import { vuexTypes } from '@vuex/types'

  import {dispatchAppEvent, attachEventHandler} from '@js/events/helpers'
  import { commonEvents } from '@js/events/common_events'
  export default {
    name: 'app',

    components: {
      Navbar,
      MainMenu,
      AdditionalMenu,
      // Sidebar,
      Snackbar,
      InactivityMessage,
      FileViewer,
      LoaderBar
    },

    data: () => ({
      menuVisible: false,
      showPopup: false
    }),

    computed: {
      ...mapGetters([
        vuexTypes.userAccountId,
        vuexTypes.isLoggedIn,
        vuexTypes.userEmail,
        vuexTypes.isForcedMenuHidden
      ]),
      isDashboardRoute () {
        return this.$route.name === 'dashboard.index'
        // return this.$route.name === 'dashboard.test'
      }
    },

    mounted () {
      if (this.isLoggedIn) {
        this.performLoggedInActions()
      } else {
        this.performLoggedOutActions()
      }

      this.addMenu()
    },

    created () {
      window.setTimeout(() => {
        this.$store.commit(vuexTypes.KEEP_SESSION)
      }, 1000)
      this.subscribeToUserLogout()
      attachEventHandler(commonEvents.showTfaRecovery, () => {
        this.$router.push({name: 'settings.security', params: {tfaRecovery: true}})
      })
    },

    methods: {
      ...mapActions({
        loadAccount: vuexTypes.GET_ACCOUNT_DETAILS,
        loadBalances: vuexTypes.GET_ACCOUNT_BALANCES,
        SET_HIDDEN_MENU_FORCED: 'SET_HIDDEN_MENU_FORCED'
      }),
      performLoggedInActions () {
        this.SET_HIDDEN_MENU_FORCED(false)
        dispatchAppEvent(commonEvents.enterAppEvent)
        this.loadUserData()
      },
      performLoggedOutActions () {
        /**
         * Here can be some conversion tracking logic for site visitors
         */
      },
      loadUserData () {
        this.loadAccount()
        this.loadBalances()
      },
      subscribeToUserLogout () {
        this.$store.subscribe(mutation => {
          switch (mutation.type) {
            case vuexTypes.SET_LOGGED_OUT_STATE:
              this.SET_HIDDEN_MENU_FORCED(true)
              this.removeMenu()
              this.menuVisible = false
              if (this.$route.name !== vueRoutes.login.name) {
                this.$router.replace(vueRoutes.login)
              }
              break
          }
        })
      },
      hideSidebar (status) {
        this.menuVisible = status
      },
      closePopup (status) {
        this.showPopup = status
      },
      openPopup () {
        this.showPopup = true
      },
      addMenu () {
        this.$nextTick(() => {
          const menuContainer = this.$refs.menuContainer
          if (!menuContainer) return
          document.body.appendChild(menuContainer)
        })
      },
      removeMenu () {
        const menuContainer = this.$refs.menuContainer || document.querySelector('.app-menu-container')
        if (menuContainer && menuContainer.parentNode) {
          menuContainer.parentNode.removeChild(menuContainer)
        }
      }
    },

    watch: {
      async isLoggedIn (value) {
        if (!value) {
          this.removeMenu()
          this.menuVisible = false
          return
        }
        this.SET_HIDDEN_MENU_FORCED(false)
        await this.$nextTick()
        this.addMenu()
      }
    }
  }
</script>

<style lang="scss">
  @import '~scss/mixins';
  @import '~scss/variables';

  .md-app {
    height: 100vh;

    &.new-dashboard-view {
      background: url('~/static/img/dashboard_bg.svg') center/cover no-repeat;

      .md-drawer.md-persistent-mini {
        background: transparent;
        transform: translate3D(0, 59px, 0);
      }

      .md-app-content {
        background: rgba(237, 237, 237, 0.5);
        backdrop-filter: blur(90px);
        -webkit-backdrop-filter: blur(90px);
        padding: 20px 50px 20px 125px;
      }

      &.md-fixed .md-app-scroller {
        padding-left: 0px !important;
      }
    }
  }

  .md-drawer {
    width: 340px;
    max-width: calc(100vw - 75px);
    @include border(1px, 0);

    @include respond-to(small) {
      height: 100%;
      overflow: auto;
    }
  }

  .mobile__menu-icon {
    display: none;

    @include respond-to(medium-tablet) {
      display: initial;
    }
  }

  .app-menu-container {
    position: absolute;
    top: 80px;
    left: 20px;
    max-height: 100vh;
    width: min-content;
    display: flex;
    flex-direction: column;
    gap: 40px;
    z-index: 2;
  }

</style>



// WEBPACK FOOTER //
// src/vue/root/App.vue